import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Field, FormError } from '@/components/app/common';
import { createStore } from '@/lib/store';
import { del, post, put } from '@/lib/api';
import { addDays, todayIn } from '@/lib/format';
import { notify } from '@/lib/notify';
import type { EventData, Status } from '@/lib/types';
import { TimeEditor, timeFromEvent, timePayload, timeValid, type TimeValue } from './TimeEditor';

export interface EventDialogState {
  feedId: string;
  feedTz: string;
  /** Existing own event to edit. */
  event?: EventData & { id: string; revision: number | null };
  /** Fields to start a new event with (e.g. from quick create). */
  preset?: Partial<EventData>;
  onDone: () => void;
}

const store = createStore<EventDialogState | null>(null);
export const openEventDialog = (s: EventDialogState) => store.set(s);

const REPEATS: [string, string][] = [
  ['', 'Does not repeat'],
  ['FREQ=DAILY', 'Every day'],
  ['FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', 'Every weekday (Mon–Fri)'],
  ['FREQ=WEEKLY', 'Every week'],
  ['FREQ=WEEKLY;INTERVAL=2', 'Every 2 weeks'],
  ['FREQ=MONTHLY', 'Every month'],
  ['FREQ=YEARLY', 'Every year'],
  ['custom', 'Custom rule…'],
];

function splitRrule(r: string) {
  if (!r) return { base: '', until: '', count: '' };
  const parts = r.split(';');
  const until = parts.find((p) => p.startsWith('UNTIL='))?.slice(6) || '';
  const count = parts.find((p) => p.startsWith('COUNT='))?.slice(6) || '';
  const base = parts.filter((p) => !p.startsWith('UNTIL=') && !p.startsWith('COUNT=')).join(';');
  return { base, until: until ? `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}` : '', count };
}

const selectCls = 'border-input dark:bg-input/30 h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

export function StatusPicker({ value, onChange }: { value: Status; onChange: (s: Status) => void }) {
  return (
    <ToggleGroup type="single" variant="outline" size="sm" value={value} onValueChange={(v) => v && onChange(v as Status)} aria-label="Status" className="w-fit">
      <ToggleGroupItem value="confirmed" className="px-3">Confirmed</ToggleGroupItem>
      <ToggleGroupItem value="tentative" className="px-3">Tentative</ToggleGroupItem>
      <ToggleGroupItem value="cancelled" className="px-3">Cancelled</ToggleGroupItem>
    </ToggleGroup>
  );
}

export function EventDialog() {
  const s = store.use();
  return <Dialog open={!!s} onOpenChange={(o) => !o && store.set(null)}>{s ? <EventForm key={s.event?.id ?? JSON.stringify(s.preset)} s={s} /> : null}</Dialog>;
}

function EventForm({ s }: { s: EventDialogState }) {
  const ev = s.event;
  const tomorrow = addDays(todayIn(s.feedTz), 1);
  const base: Partial<EventData> = ev ?? { all_day: false, start: `${tomorrow}T09:00`, end: `${tomorrow}T10:00`, timezone: s.feedTz, status: 'confirmed', rrule: '', ...(s.preset ?? {}) };
  const rr = splitRrule(base.rrule ?? '');
  const known = REPEATS.some(([v]) => v === rr.base);
  const [title, setTitle] = useState(base.title ?? '');
  const [time, setTime] = useState<TimeValue>(() => timeFromEvent(base as EventData, s.feedTz));
  const [repeat, setRepeat] = useState(known ? rr.base : 'custom');
  const [custom, setCustom] = useState(known ? '' : rr.base);
  const [ends, setEnds] = useState(rr.count ? 'count' : rr.until ? 'until' : 'never');
  const [until, setUntil] = useState(rr.until);
  const [count, setCount] = useState(rr.count || '10');
  const [location, setLocation] = useState(base.location ?? '');
  const [moreOpen, setMoreOpen] = useState(!!(base.description || base.url));
  const [description, setDescription] = useState(base.description ?? '');
  const [url, setUrl] = useState(base.url ?? '');
  const [status, setStatus] = useState<Status>((base.status as Status) ?? 'confirmed');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setErr(''), [title, time, repeat, custom, location]);

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!title.trim()) return setErr('Please add a title.');
    if (!timeValid(time)) return setErr('Please check the start and end.');
    let rrule = repeat === 'custom' ? custom.trim().toUpperCase() : repeat;
    if (rrule) {
      if (ends === 'count' && count) rrule += `;COUNT=${Number(count)}`;
      else if (ends === 'until' && until) rrule += `;UNTIL=${until.replace(/-/g, '')}T235959Z`;
    }
    const payload = { title, location, description, url, status, rrule, ...timePayload(time), ...(ev ? { revision: ev.revision } : {}) };
    setBusy(true);
    try {
      if (ev) await put(`/feeds/${s.feedId}/events/${ev.id}`, payload);
      else await post(`/feeds/${s.feedId}/events`, payload);
      notify(ev ? 'Changes saved' : 'Event added');
      store.set(null);
      s.onDone();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!ev) return;
    store.set(null);
    try {
      await del(`/feeds/${s.feedId}/events/${ev.id}`);
      notify(`Deleted “${ev.title}”`, {
        undo: async () => {
          await post(`/feeds/${s.feedId}/events/${ev.id}/restore`);
          notify('Event restored');
          s.onDone();
        },
      });
      s.onDone();
    } catch (ex) {
      notify((ex as Error).message, { kind: 'error' });
    }
  }

  return (
    <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl">
      <form
        className="grid grid-cols-[minmax(0,1fr)] gap-5"
        noValidate
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{ev ? 'Edit event' : 'New event'}</DialogTitle>
        </DialogHeader>
        <Input name="title" aria-label="Title" placeholder="Add a title" maxLength={200} autoFocus value={title} onChange={(e) => setTitle(e.target.value)} className="h-11 text-lg font-medium" />
        <TimeEditor value={time} onChange={setTime} feedTz={s.feedTz} idPrefix="ev" />
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2">
          <Field label="Repeat" htmlFor="ev-repeat">
            <select id="ev-repeat" name="repeat" className={selectCls} value={repeat} onChange={(e) => setRepeat(e.target.value)}>
              {REPEATS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Location" htmlFor="ev-location">
            <Input id="ev-location" name="location" maxLength={300} placeholder="Optional" value={location} onChange={(e) => setLocation(e.target.value)} />
          </Field>
        </div>
        {repeat === 'custom' ? (
          <Field label="Rule (RFC 5545)" htmlFor="ev-custom">
            <Input id="ev-custom" name="custom_rrule" placeholder="FREQ=WEEKLY;BYDAY=TU,TH" value={custom} onChange={(e) => setCustom(e.target.value)} className="font-mono" />
          </Field>
        ) : null}
        {repeat ? (
          <fieldset className="grid grid-cols-[minmax(0,1fr)] gap-2">
            <legend className="mb-2 text-sm font-medium">Ends</legend>
            <RadioGroup value={ends} onValueChange={setEnds} className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="never" /> Never
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="until" /> On
                <Input type="date" name="until" aria-label="End date" className="h-8 w-40" value={until} onFocus={() => setEnds('until')} onChange={(e) => setUntil(e.target.value)} />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="count" /> After
                <Input type="number" name="count" min={1} max={999} aria-label="Number of times" className="h-8 w-20" value={count} onFocus={() => setEnds('count')} onChange={(e) => setCount(e.target.value)} />
                times
              </label>
            </RadioGroup>
          </fieldset>
        ) : null}
        {moreOpen ? (
          <>
            <Field label="Notes" htmlFor="ev-notes" hint="Visible to subscribers. Avoid students’ full names and medical or contact details.">
              <Textarea id="ev-notes" name="description" rows={3} maxLength={4000} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>
            <Field label="Link" htmlFor="ev-url">
              <Input id="ev-url" name="url" type="url" placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
            </Field>
          </>
        ) : (
          <button type="button" className="text-primary w-fit text-sm font-medium hover:underline" onClick={() => setMoreOpen(true)}>
            + Add notes or a link
          </button>
        )}
        <Field label="Status" hint="Cancelled events stay on subscribers’ calendars, marked as cancelled.">
          <StatusPicker value={status} onChange={setStatus} />
        </Field>
        <FormError>{err}</FormError>
        <DialogFooter className="items-center sm:justify-between">
          {ev ? (
            <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" onClick={remove}>
              Delete
            </Button>
          ) : (
            <span className="text-muted-foreground hidden text-xs sm:block">Tip: Ctrl/⌘ + Enter to save</span>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => store.set(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : ev ? 'Save changes' : 'Add event'}
            </Button>
          </div>
        </DialogFooter>
        <Label className="sr-only">Event form</Label>
      </form>
    </DialogContent>
  );
}
